import { Button } from '@mantine/core';
import type { TFunction } from 'i18next';
import { Fragment, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { File, FormView, MessageResponse, SingleMessageResponse } from '@/api';
import { ConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import {
  downloadText,
  formatBytes,
  isNostr,
  type MessageData,
  messageAsJson,
  messageAsText,
  parseMessageData,
  SPAM_THRESHOLD,
  senderFromData,
  signalHits,
  valueText,
  verdictLabel,
  verdictOf,
} from '@/lib/messages';
import { getCachedDecryptedMessage } from '@/lib/nostr-decrypt';
import { absoluteTime, parseTimestamp } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './Inbox.module.css';
import { NostrDecryptCard } from './NostrDecryptCard';

export interface ReadingPaneProps {
  id: string;
  formTitle: (formId: string) => string | undefined;
  decrypted?: MessageData;
  onDecrypted: (id: string, data: MessageData) => void;
  onUpdated: (message: MessageResponse) => void;
  onDeleted: (id: string) => void;
}

type Detail =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'ready'; data: SingleMessageResponse };

/** Mount with `key={id}`: state belongs to one message. */
export function ReadingPane({
  id,
  formTitle,
  decrypted,
  onDecrypted,
  onUpdated,
  onDeleted,
}: ReadingPaneProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];
  const notify = useUndoToast();
  const [detail, setDetail] = useState<Detail>({ status: 'loading' });
  const [busy, setBusy] = useState(false);

  const onLoaded = useEffectEvent((message: MessageResponse) => {
    onUpdated(message);
    void useRustyState.getState().refreshUnreadCount();
    if (isNostr(message) && !decrypted) {
      void getCachedDecryptedMessage(message.id).then((data) => {
        if (data) onDecrypted(message.id, data);
      });
    }
  });

  useEffect(() => {
    let ignore = false;
    // Opening is reading: the PATCH marks it read and returns the full message with files and view.
    useRustyState
      .getState()
      .api.updateMessage(id, { is_read: true }, { onSuccess: false, onError: false })
      .then((data) => {
        if (ignore) return;
        setDetail({ status: 'ready', data });
        onLoaded(data.message);
      })
      .catch(() => {
        if (!ignore) setDetail({ status: 'missing' });
      });
    return () => {
      ignore = true;
    };
  }, [id]);

  if (detail.status === 'loading') {
    return <span className={classes.sub}>{t('ui.loading')}</span>;
  }
  if (detail.status === 'missing') {
    return <span className={classes.sub}>{t('inbox.messageNotFound')}</span>;
  }

  const { message, files, view } = detail.data;
  const nostr = isNostr(message);
  const data = nostr ? (decrypted ?? null) : parseMessageData(message);
  const fromData = data ? senderFromData(data) : {};
  const name = message.sender_name ?? fromData.name;
  const email = message.sender_email ?? fromData.email;
  const sender =
    name || email || (nostr && !data ? t('inbox.encryptedSender') : t('inbox.noSender'));
  const title = formTitle(message.form_id) ?? detail.data.form?.title ?? t('inbox.unknownForm');

  const replace = (next: MessageResponse) => {
    setDetail({ status: 'ready', data: { ...detail.data, message: next } });
    onUpdated(next);
  };

  const toggleSpam = async () => {
    setBusy(true);
    try {
      const updated = await useRustyState
        .getState()
        .api.updateMessage(message.id, { is_spam: !message.is_spam }, { onSuccess: false });
      replace(updated.message);
      // Unread and weekly counts leave spam out, so a verdict change moves them.
      void useRustyState.getState().refreshUnreadCount();
      void useRustyState.getState().refreshFormsSummary();
      if (message.is_spam) {
        const n = updated.notified_recipients ?? 0;
        const parts = [
          n > 0 ? t('inbox.markedNotSpam', { count: n }) : t('inbox.markedNotSpamNone'),
        ];
        if (updated.integrations_resent) parts.push(t('inbox.resentIntegrations'));
        if (updated.auto_reply_sent) parts.push(t('inbox.resentAutoReply'));
        notify(parts.join(' '));
      } else {
        notify(t('inbox.markedSpam'));
      }
    } catch {
      // the API layer reports the error
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    await useRustyState.getState().api.deleteMessage(message.id);
    notify(t('inbox.deleted'));
    void useRustyState.getState().refreshUnreadCount();
    void useRustyState.getState().refreshFormsSummary();
    onDeleted(message.id);
  };

  const visit = visitLine(t, message, view);

  return (
    <>
      <div className={classes.paneHead}>
        <span className={classes.small}>
          <UiLink to={`/account/forms/${message.form_id}`}>{title}</UiLink> ·{' '}
          {absoluteTime(message.created_at, locale)}
        </span>
        <h2 className={classes.paneTitle}>{sender}</h2>
        {email && email !== sender && (
          <span className={`${classes.mono} ${classes.sub}`}>{email}</span>
        )}
      </div>

      {nostr && !data && (
        <NostrDecryptCard message={message} onDecrypted={(d) => onDecrypted(message.id, d)} />
      )}

      {!nostr && <VerdictBox message={message} />}

      {data && (
        <dl className={classes.dl} aria-label={t('inbox.fields')}>
          {Object.entries(data).map(([key, value]) => (
            <Fragment key={key}>
              <dt>{key}</dt>
              <dd>{valueText(value)}</dd>
            </Fragment>
          ))}
        </dl>
      )}

      {files.length > 0 && <FileList files={files} />}

      <div className={classes.actions}>
        {!nostr &&
          (message.is_spam ? (
            <Button size="xs" onClick={() => void toggleSpam()} loading={busy}>
              {t('inbox.markNotSpam')}
            </Button>
          ) : (
            <Button size="xs" onClick={() => void toggleSpam()} loading={busy}>
              {t('inbox.markSpam')}
            </Button>
          ))}
        {data && (
          <>
            <Button
              size="xs"
              onClick={() =>
                downloadText(
                  messageAsJson(data),
                  `formshive-message-${message.id}.json`,
                  'application/json'
                )
              }
            >
              {t('inbox.downloadJson')}
            </Button>
            <Button
              size="xs"
              onClick={() =>
                downloadText(messageAsText(data), `formshive-message-${message.id}.txt`)
              }
            >
              {t('inbox.downloadText')}
            </Button>
          </>
        )}
        <span className={classes.pushRight}>
          <ConfirmDelete
            label={t('inbox.delete')}
            title={t('inbox.bulkDeleteConfirm', { count: 1 })}
            description={t('inbox.deleteDescription')}
            confirmLabel={t('inbox.bulkDeleteConfirm', { count: 1 })}
            onConfirm={remove}
            size="xs"
          />
        </span>
      </div>

      {visit && <div className={classes.small}>{visit}</div>}
    </>
  );
}

function VerdictBox({ message }: { message: MessageResponse }) {
  const { t, i18n } = useTranslation();
  const filterOn = useRustyState(
    (s) => s.formsSummary?.find((f) => f.id === message.form_id)?.filter_spam
  );
  const verdict = verdictOf(message, filterOn);
  const hits = signalHits(message);

  if (verdict.kind === 'encrypted') return null;
  if (verdict.kind === 'unscored') {
    return (
      <div className={classes.verdict}>
        <div className={classes.verdictHead}>
          <strong>{t('ui.verdict.unscored')}</strong>
        </div>
        <span className={classes.sub}>{t('inbox.unscoredText')}</span>
      </div>
    );
  }

  const spam = verdict.kind === 'spam';
  const label = verdictLabel(verdict);
  return (
    <div className={spam ? `${classes.verdict} ${classes.verdictSpam}` : classes.verdict}>
      <div className={classes.verdictHead}>
        <strong>
          {verdict.byYou ? t(label.key, label.values) : spam ? t('inbox.spam') : t('inbox.notSpam')}
        </strong>
        {verdict.scored && (
          <span className={classes.small}>
            {t('inbox.scoreLine', { score: verdict.score, threshold: SPAM_THRESHOLD })}
          </span>
        )}
      </div>
      <span className={classes.sub}>
        {spam
          ? hits.length > 0
            ? t('inbox.spamText')
            : t('inbox.spamTextNoSignals')
          : t('inbox.notSpamText')}
      </span>
      {spam && hits.length > 0 && (
        <ul className={classes.hits}>
          {hits.map((h) => (
            <li key={h.signal}>
              {h.signal !== 'unknown' && i18n.exists(`inbox.signals.${h.signal}`)
                ? t(`inbox.signals.${h.signal}`)
                : t('inbox.signals.unknown', { name: h.signal })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileList({ files }: { files: File[] }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  const download = async (fileId: string) => {
    setFailed(false);
    try {
      const url = await useRustyState.getState().api.downloadFile(fileId);
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className={classes.fileRow}>
      <span className={classes.small}>{t('inbox.filesLabel')}</span>
      {files.map((f) => (
        <Button key={f.id} size="xs" onClick={() => void download(f.id)}>
          {f.filename} · {formatBytes(f.size)}
        </Button>
      ))}
      {failed && (
        <p className={classes.error} role="alert">
          {t('inbox.fileFailed')}
        </p>
      )}
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function duration(t: TFunction, ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return t('inbox.seconds', { count: s });
  const m = Math.round(s / 60);
  if (m < 60) return t('inbox.minutes', { count: m });
  const h = Math.round(m / 60);
  if (h < 48) return t('inbox.hours', { count: h });
  return t('inbox.days', { count: Math.round(h / 24) });
}

function visitLine(t: TFunction, message: MessageResponse, view?: FormView): string {
  const device = view?.device_type ?? message.device_type;
  const browser = view?.browser_name ?? message.browser_name;
  const country = (message.location ?? view?.location)?.country_name;
  const parts = [device ? capitalize(device) : null, browser, country];
  if (view?.created_at) {
    const ms =
      parseTimestamp(message.created_at).getTime() - parseTimestamp(view.created_at).getTime();
    if (ms >= 0) parts.push(t('inbox.visitTime', { time: duration(t, ms) }));
  }
  return parts.filter(Boolean).join(' · ');
}
